/**
 * portal-berechtigungen/types.ts — Bereich 6 (Kundenportal-Berechtigungsgruppen,
 * Berechtigungskonzept V4.0 Kap. 8) web contract.
 *
 * RECONCILED to the backend's committed contract (the deployed source of truth):
 *   - Controller prefix `admin/customer-portal`; RBAC key `portal_access.invite`.
 *   - Customer = a polymorphic (subjectType, subjectId) pair (person | firma), NOT a
 *     single :id.
 *   - camelCase wire fields; EVERY mutation returns the full `{ mode, groups }` snapshot,
 *     which the web adopts wholesale as its new state.
 *   - Category + member editing is GRANULAR (POST/DELETE .../kategorien + .../mitglieder),
 *     NOT replace-set. Members are keyed by `grantId` (null = the primary holder).
 *
 * The wire DTOs come from @brokly/shared (portal-permission-groups.ts) — imported here,
 * NOT re-declared web-side. This module only adds the web-local UI helpers (the member
 * pool option + the IGB2B family headings for the picker).
 *
 * [Source: apps/api/src/modules/customer-portal/portal-visibility.controller.ts]
 * [Source: apps/api/src/modules/customer-portal/portal-grant-admin.controller.ts] (member pool)
 * [Source: packages/shared/src/portal-permission-groups.ts]
 */

export type {
  PortalVisibilityMode,
  Igb2bKategorieDto,
  PortalGroupMemberDto,
  PortalGroupDto,
  PortalVisibilityModeDto,
  PortalGroupsResponseDto,
} from '@brokly/shared';
export { PORTAL_VISIBILITY_MODE, PORTAL_VISIBILITY_MODE_VALUES } from '@brokly/shared';

/**
 * PortalMemberOption — one assignable portal user for the member picker. There is NO
 * global "portal users" pool endpoint; the pool is sourced from the existing portal
 * grants admin endpoint (GET /admin/customer-portal/grants/:type/:id) PLUS the always-
 * present primary holder. `grantId` null = the primary holder (Kunde); a non-null
 * `grantId` = a portal grant (Treuhaender / weitere Person). Revoked grants are excluded.
 */
export interface PortalMemberOption {
  grantId: string | null;
  label: string;
  email: string;
  /** 'primary' (the holder) | the grant status ('invited' | 'active' | ...). */
  status: string;
}

/** Sentinel grant-ref for the primary holder in member routes + UI selection sets. */
export const PRIMARY_MEMBER_REF = 'primary' as const;

/** grantId (null | UUID) -> grantRef ('primary' | UUID) — the DELETE .../mitglieder path token. */
export function memberRefOf(grantId: string | null): string {
  return grantId ?? PRIMARY_MEMBER_REF;
}

/** grantRef ('primary' | UUID) -> grantId (null | UUID) — the POST .../mitglieder body value. */
export function grantIdOfRef(ref: string): string | null {
  return ref === PRIMARY_MEMBER_REF ? null : ref;
}

/** One IGB2B Branchenfamilie heading (the category picker groups by these). */
export interface BranchenFamilie {
  /** The family-head branchencode ('10000' … '90000') — equals Igb2bKategorieDto.familie. */
  key: string;
  /** German display label (direct umlauts). */
  label: string;
}

/**
 * The 9 IGB2B Branchenfamilien, in code order, keyed by the family-head branchencode the
 * backend places in `Igb2bKategorieDto.familie` (NOT a synthetic string key). Labels are
 * display-only headings.
 */
export const IGB2B_FAMILIEN: readonly BranchenFamilie[] = [
  { key: '10000', label: 'Fahrzeuge' },
  { key: '20000', label: 'Haftpflicht' },
  { key: '30000', label: 'Unfall' },
  { key: '40000', label: 'Kranken' },
  { key: '50000', label: 'Transport' },
  { key: '60000', label: 'Sach' },
  { key: '70000', label: 'Technische Versicherungen' },
  { key: '80000', label: 'Übrige Branchen' },
  { key: '90000', label: 'Leben & Finanzprodukte' },
] as const;

/** branchencode -> family-head code ('10000' … '90000'), the Igb2bKategorieDto.familie value. */
export function familieForBranchencode(branchencode: string): string {
  const block = Math.floor(Number(branchencode) / 10000);
  return `${block}0000`;
}
